import { useState, useEffect, useCallback, useRef } from 'react';

interface ImageCacheEntry {
  url: string;
  timestamp: number;
  loading: boolean;
  error?: string;
}

class ImageCacheManager {
  private static instance: ImageCacheManager;
  private cache = new Map<string, ImageCacheEntry>();
  private preloadQueue = new Set<string>();
  private readonly maxCacheSize = 500;
  private readonly cacheTimeout = 30 * 60 * 1000; // 30 minutes

  public static getInstance(): ImageCacheManager {
    if (!ImageCacheManager.instance) {
      ImageCacheManager.instance = new ImageCacheManager();
    }
    return ImageCacheManager.instance;
  }

  private constructor() {
    // Clean up expired entries periodically
    setInterval(() => this.cleanup(), 5 * 60 * 1000); // Every 5 minutes
  }

  private cleanup() {
    const now = Date.now();
    const expiredKeys = Array.from(this.cache.entries())
      .filter(([, entry]) => now - entry.timestamp > this.cacheTimeout)
      .map(([key]) => key);
    
    expiredKeys.forEach(key => this.cache.delete(key));
  }

  private ensureCacheSize() {
    if (this.cache.size >= this.maxCacheSize) {
      // Remove oldest entries
      const entries = Array.from(this.cache.entries())
        .sort(([, a], [, b]) => a.timestamp - b.timestamp);
      
      const toRemove = entries.slice(0, this.cache.size - this.maxCacheSize + 100);
      toRemove.forEach(([key]) => this.cache.delete(key));
    }
  }

  public getCachedImage(url: string): ImageCacheEntry | null {
    if (!url) return null;
    
    const entry = this.cache.get(url);
    if (!entry) return null;

    // Check if entry is expired
    if (Date.now() - entry.timestamp > this.cacheTimeout) {
      this.cache.delete(url);
      return null;
    }

    return entry;
  }

  public setCachedImage(url: string, imageUrl: string, loading = false, error?: string) {
    if (!url) return;
    
    this.ensureCacheSize();
    this.cache.set(url, {
      url: imageUrl,
      timestamp: Date.now(),
      loading,
      error
    });
  }

  public preloadImage(url: string): Promise<string> {
    if (!url) return Promise.reject('No URL provided');
    
    const cached = this.getCachedImage(url);
    if (cached && !cached.loading && !cached.error) {
      return Promise.resolve(cached.url);
    }

    // Prevent duplicate preloads
    if (this.preloadQueue.has(url)) {
      return new Promise((resolve, reject) => {
        const checkCache = () => {
          const entry = this.getCachedImage(url);
          if (entry && !entry.loading) {
            if (entry.error) {
              reject(new Error(entry.error));
            } else {
              resolve(entry.url);
            }
          } else {
            setTimeout(checkCache, 50);
          }
        };
        checkCache();
      });
    }

    this.preloadQueue.add(url);
    this.setCachedImage(url, url, true);

    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.setCachedImage(url, url, false);
        this.preloadQueue.delete(url);
        resolve(url);
      };
      
      img.onerror = () => {
        this.setCachedImage(url, url, false, 'Failed to load image');
        this.preloadQueue.delete(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }

  public batchPreloadImages(urls: Array<string>): Promise<Array<{ url: string; success: boolean; error?: string }>> {
    const promises = urls.map(async (url) => {
      try {
        await this.preloadImage(url);
        return { url, success: true };
      } catch (error) {
        return { 
          url, 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        };
      }
    });

    return Promise.all(promises);
  }

  public clearCache() {
    this.cache.clear();
    this.preloadQueue.clear();
  }

  public getCacheStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      queueSize: this.preloadQueue.size
    };
  }
}

export interface UseImageCacheOptions {
  preload?: boolean;
  fallbackUrl?: string;
}

export function useImageCache(urls: Array<string>, options: UseImageCacheOptions = {}) {
  const { preload = true, fallbackUrl } = options;
  const [cachedUrls, setCachedUrls] = useState<Record<string, string>>({});
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const cache = useRef(ImageCacheManager.getInstance());
  const prevUrlsRef = useRef<string>('');

  // Memoize URLs to prevent unnecessary re-processing
  const urlsString = JSON.stringify(urls.sort());
  const shouldUpdate = urlsString !== prevUrlsRef.current;

  const updateStates = useCallback(() => {
    const newCachedUrls: Record<string, string> = {};
    const newLoadingStates: Record<string, boolean> = {};
    const newErrors: Record<string, string> = {};

    urls.forEach(url => {
      if (!url) {
        newCachedUrls[url] = fallbackUrl || '';
        return;
      }

      const cached = cache.current.getCachedImage(url);
      if (cached) {
        newCachedUrls[url] = cached.url;
        newLoadingStates[url] = cached.loading;
        if (cached.error) {
          newErrors[url] = cached.error;
        }
      } else {
        newCachedUrls[url] = fallbackUrl || '';
        newLoadingStates[url] = false;
      }
    });

    setCachedUrls(newCachedUrls);
    setLoadingStates(newLoadingStates);
    setErrors(newErrors);
  }, [urls, fallbackUrl]);

  // Only update when URLs actually change
  useEffect(() => {
    if (!shouldUpdate) return;
    
    prevUrlsRef.current = urlsString;
    
    // Update states immediately with cached data
    updateStates();

    if (preload && urls.length > 0) {
      const validUrls = urls.filter(Boolean);
      const uncachedUrls = validUrls.filter(url => !cache.current.getCachedImage(url));
      
      if (uncachedUrls.length > 0) {
        // Batch preload uncached images
        cache.current.batchPreloadImages(uncachedUrls).then(() => {
          // Update states after preloading completes
          updateStates();
        });
      }
    }
  }, [shouldUpdate, preload, updateStates]);

  const getCachedUrl = useCallback((url: string): string => {
    if (!url) return fallbackUrl || '';
    return cachedUrls[url] || fallbackUrl || '';
  }, [cachedUrls, fallbackUrl]);

  const isLoading = useCallback((url: string): boolean => {
    return loadingStates[url] || false;
  }, [loadingStates]);

  const getError = useCallback((url: string): string | undefined => {
    return errors[url];
  }, [errors]);

  const preloadImage = useCallback((url: string) => {
    return cache.current.preloadImage(url);
  }, []);

  const clearCache = useCallback(() => {
    cache.current.clearCache();
    setCachedUrls({});
    setLoadingStates({});
    setErrors({});
  }, []);

  return {
    getCachedUrl,
    isLoading,
    getError,
    preloadImage,
    clearCache,
    cacheStats: cache.current.getCacheStats()
  };
}

export default useImageCache;